// The meeting screen (`/c/{slug}/cadence/meeting/{id}`; 06 §Cadence "Meeting screen"): one
// meeting of a series, run from here.
//   - Header: the series, when, its state; Start / End (End asks for a 1–10 rating and notes);
//     Cancel (ACTIONS: a meeting not started yet, after a confirm; it stays, cancelled);
//     the previous and next meeting.
//   - Agenda: by section (scorecard misses → off-track priorities → issues → to-dos →
//     headlines → custom), each line with its detail and time box; section timers (the live
//     line's time runs from `item_started_at`; a section turns amber, then red, once over its
//     box). "Build agenda" rebuilds it from the live data, "Add item" adds a line; each line's
//     hover icons mark it done, move it, edit its time box or remove it (custom lines). A click
//     on a line makes it current (while live) and shows it in the side panel.
//   - Side panel: the line's source as a live card (a metric's week and 8-week trend, a
//     priority with its milestones, an issue, a to-do), then inline outcome capture (+ To-do,
//     + Decision, Solve issue, + Issue) and the outcomes captured so far.
//   - Pre-read: its state, the digital employee asked (and whether it is live, honestly), "Ask
//     for the pre-read now", the note. Notes: autosaved; the rating once ended.
//   - Ended: where the summary was posted (the team channel's topic "Meeting YYYY-MM-DD").
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  buttonVariants,
  Checkbox,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Sparkline,
  Textarea,
  toast,
  ViewTabs,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarClockIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleDotIcon,
  ClockIcon,
  FlagIcon,
  HammerIcon,
  MessageSquareTextIcon,
  PlayIcon,
  PlusIcon,
  SquareIcon,
  ThumbsUpIcon,
  Trash2Icon,
  WandSparklesIcon,
  XIcon,
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { AgendaItemView, AgendaSourceCard, MeetingDetail } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { topicsQuery } from "../chat/chat-queries";
import type { CompanyAccess } from "../company";
import { formatCardMetric } from "../format";
import { Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { ErrorNotice } from "../structure/parts";
import {
  ageLabel,
  clock,
  defaultDue,
  dueInstant,
  groupAgenda,
  itemElapsed,
  MEETING_STATE,
  meetingWhen,
  minutes,
  PREREAD_STATE,
  PRIORITY_LABELS,
  SECTION_LABELS,
  type TimerTone,
} from "./cadence-model";
import { HOVER_STRIP, IconAction } from "./cadence-parts";
import { cadenceKeys, meetingQuery, type SeatOption, useSeatOptions } from "./cadence-queries";
import { checkDaysOf, ExpectedFields } from "./issue-dialogs";

export type MeetingTab = "agenda" | "preread" | "notes";

const TONE_TEXT: Record<TimerTone, string> = {
  ok: "text-muted-foreground",
  over: "text-warning-text",
  late: "text-danger-text",
};

type Meeting = MeetingDetail["meeting"];

/** Writes that answer the meeting: the answer replaces the cached one. */
function useMeetingWrite<V>(
  co: string,
  id: string,
  fn: (v: V) => Promise<MeetingDetail | undefined>,
  onDone?: (v: V) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (d, v) => {
      if (d) qc.setQueryData(cadenceKeys.meeting(co, id), d);
      else void qc.invalidateQueries({ queryKey: cadenceKeys.meeting(co, id) });
      void qc.invalidateQueries({ queryKey: cadenceKeys.series(co) });
      onDone?.(v);
    },
  });
}

export function MeetingScreen({
  api,
  company,
  id,
  tab,
  onTab,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
  tab: MeetingTab;
  onTab: (tab: MeetingTab) => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useQuery(meetingQuery(api, co, id));
  const [selected, setSelected] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const d = q.data;
  const m = d?.meeting;
  const live = m?.state === "live";
  const now = useNow(live);

  const start = useMeetingWrite(co, id, () => api.client.meetings.start({ params: { co, id } }));
  // ACTIONS: a meeting not started yet is cancelled (kept; the series schedules its next one).
  const [cancelling, setCancelling] = useState(false);
  const cancel = useMeetingWrite(
    co,
    id,
    () => api.client.meetings.cancel({ params: { co, id } }),
    () => setCancelling(false),
  );
  const current = useMutation({
    mutationFn: (item: string) =>
      api.client.meetings.update({ params: { co, id }, body: { current_item_id: item } }),
    onMutate: (item) => {
      // On screen at once: the old line keeps its running time, the new one starts now.
      qc.setQueryData<MeetingDetail>(cadenceKeys.meeting(co, id), (prev) => {
        if (!prev) return prev;
        const at = new Date().toISOString();
        return {
          ...prev,
          meeting: { ...prev.meeting, current_item_id: item, item_started_at: at },
          agenda: prev.agenda.map((i) =>
            i.id === prev.meeting.current_item_id
              ? { ...i, elapsed_s: itemElapsed(i, prev.meeting, Date.now()) }
              : i,
          ),
        };
      });
    },
    onSuccess: (detail) => qc.setQueryData(cadenceKeys.meeting(co, id), detail),
    onError: () => void qc.invalidateQueries({ queryKey: cadenceKeys.meeting(co, id) }),
  });

  if (q.isPending)
    return (
      <div className="p-6">
        <Loading label="Loading the meeting" rows={6} />
      </div>
    );
  if (q.isError || !d || !m)
    return (
      <div className="p-6">
        <ErrorNotice what="The meeting" error={q.error} />
      </div>
    );

  const state = MEETING_STATE[m.state];
  const selectedId = selected ?? m.current_item_id ?? d.agenda[0]?.id ?? null;
  const item = d.agenda.find((i) => i.id === selectedId) ?? null;
  const select = (i: AgendaItemView) => {
    setSelected(i.id);
    if (live && d.can_edit && m.current_item_id !== i.id) current.mutate(i.id);
  };
  const nav = (to: string | null, label: string, icon: ReactNode) =>
    to ? (
      <Link
        to="/c/$slug/kpis/meetings/$meetingId"
        params={{ slug: company.slug, meetingId: to }}
        search={keepFocus}
        aria-label={label}
        title={label}
        className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
      >
        {icon}
      </Link>
    ) : (
      <Button variant="ghost" size="icon-sm" aria-label={label} disabled>
        {icon}
      </Button>
    );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-slot="meeting" data-state={m.state}>
      <LocationHeader
        icon={CalendarClockIcon}
        crumbs={[
          {
            label: "Meetings",
            render: (
              <Link
                to="/c/$slug/kpis/meetings"
                params={{ slug: company.slug }}
                search={keepFocus}
              />
            ),
          },
        ]}
        title={d.series.name}
        badge={<Badge variant={state.badge}>{state.label}</Badge>}
        actions={
          <>
            {nav(d.previous_meeting_id, "Previous meeting", <ChevronLeftIcon aria-hidden="true" />)}
            {nav(d.next_meeting_id, "Next meeting", <ChevronRightIcon aria-hidden="true" />)}
            {d.can_edit && m.state === "scheduled" ? (
              <Button size="sm" disabled={start.isPending} onClick={() => start.mutate(undefined)}>
                <PlayIcon data-icon="inline-start" aria-hidden="true" />
                Start meeting
              </Button>
            ) : null}
            {d.can_edit && m.state === "scheduled" ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={cancel.isPending}
                onClick={() => setCancelling(true)}
              >
                <XIcon data-icon="inline-start" aria-hidden="true" />
                Cancel meeting
              </Button>
            ) : null}
            {d.can_edit && live ? (
              <Button size="sm" variant="destructive" onClick={() => setEnding(true)}>
                <SquareIcon data-icon="inline-start" aria-hidden="true" />
                End meeting
              </Button>
            ) : null}
          </>
        }
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-subtle px-6 py-2 text-xs text-muted-foreground max-md:px-4">
        <span className="font-medium text-foreground" data-slot="meeting-when">
          {meetingWhen(m.scheduled_at, company.calendar?.timezone)}
        </span>
        <span>{d.series.team_name ?? "Company"}</span>
        <span>{d.series.duration_minutes} min</span>
        {d.channel ? <span>Summary to #{d.channel.name}</span> : null}
        {live && m.started_at ? (
          <span className="inline-flex items-center gap-1 text-success-text tabular-nums">
            <ClockIcon aria-hidden="true" className="size-3.5" />
            {clock((now - Date.parse(m.started_at)) / 1000)} running
          </span>
        ) : null}
        {m.state === "ended" && m.rating ? <span>Rated {m.rating}/10</span> : null}
      </div>
      {m.state === "ended" ? <SummaryNotice api={api} company={company} detail={d} /> : null}
      <ViewTabs
        aria-label="Meeting"
        className="px-6 max-md:px-4"
        value={tab}
        onValueChange={(v) => onTab(v as MeetingTab)}
        tabs={[
          { value: "agenda", label: "Agenda", count: d.agenda.length },
          { value: "preread", label: "Pre-read" },
          { value: "notes", label: "Notes" },
        ]}
      />
      {tab === "agenda" ? (
        <div className="flex min-h-0 flex-1 max-lg:flex-col max-lg:overflow-auto">
          <div className="min-h-0 min-w-0 flex-1 overflow-auto px-6 pt-4 pb-10 max-lg:overflow-visible max-md:px-4">
            <Agenda
              api={api}
              co={co}
              detail={d}
              now={now}
              selected={selectedId}
              onSelect={select}
            />
          </div>
          <aside
            aria-label="Agenda item"
            data-slot="meeting-panel"
            className="w-[380px] shrink-0 overflow-auto border-l border-subtle bg-sunken/40 px-5 pt-4 pb-10 max-lg:w-full max-lg:border-t max-lg:border-l-0"
          >
            <SidePanel api={api} company={company} detail={d} item={item} />
          </aside>
        </div>
      ) : tab === "preread" ? (
        <div className="min-h-0 flex-1 overflow-auto px-6 pt-4 pb-10 max-md:px-4">
          <PreRead api={api} co={co} detail={d} />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto px-6 pt-4 pb-10 max-md:px-4">
          <Notes api={api} co={co} detail={d} />
        </div>
      )}
      {ending ? <EndDialog api={api} co={co} meeting={m} onClose={() => setEnding(false)} /> : null}
      <AlertDialog open={cancelling} onOpenChange={setCancelling}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              It stays in the list as cancelled, with its agenda. The series goes on: its next
              meeting is scheduled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate(undefined)}
            >
              Cancel meeting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** A clock that ticks every second while `on`. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!on) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [on]);
  return now;
}

function SummaryNotice({
  api,
  company,
  detail: d,
}: {
  api: Api;
  company: CompanyAccess;
  detail: MeetingDetail;
}) {
  const ch = d.channel;
  const topics = useQuery({
    ...topicsQuery(api, company.id, ch?.id ?? ""),
    enabled: !!ch && !!d.meeting.summary_message_id,
    retry: false,
  });
  if (!ch || !d.meeting.summary_message_id) return null;
  const topic = topics.data?.data.find((t) => t.name === ch.topic);
  return (
    <Notice
      tone="success"
      icon={MessageSquareTextIcon}
      className="mx-6 mt-3 max-md:mx-4"
      data-slot="summary-notice"
      action={
        topic ? (
          <Link
            to="/c/$slug/chat/$channelId/$topicId"
            params={{ slug: company.slug, channelId: ch.id, topicId: topic.id }}
            search={keepFocus}
            className={buttonVariants({ variant: "secondary", size: "sm" })}
          >
            Open in chat
          </Link>
        ) : null
      }
    >
      The summary was posted to #{ch.name} › {ch.topic}.
    </Notice>
  );
}

// Agenda ----------------------------------------------------------------------------------

function Agenda({
  api,
  co,
  detail: d,
  now,
  selected,
  onSelect,
}: {
  api: Api;
  co: string;
  detail: MeetingDetail;
  now: number;
  selected: string | null;
  onSelect: (i: AgendaItemView) => void;
}) {
  const m = d.meeting;
  const id = m.id;
  const open = d.can_edit && m.state !== "ended" && m.state !== "cancelled";
  const groups = groupAgenda(d.agenda, m, now);
  const [adding, setAdding] = useState(false);
  // A scorecard or priority line names its seat's holder (two seats may miss the same metric).
  const holders = new Map(useSeatOptions(api, co).map((s) => [s.id, s.holder ?? s.title]));
  const build = useMeetingWrite(co, id, () =>
    api.client.meetings.buildAgenda({ params: { co, id } }),
  );
  const patch = useMeetingWrite(
    co,
    id,
    (v: { item: string; body: Parameters<Api["client"]["meetings"]["updateItem"]>[0]["body"] }) =>
      api.client.meetings.updateItem({ params: { co, id, item_id: v.item }, body: v.body }),
  );
  const remove = useMeetingWrite(co, id, async (item: string) => {
    await api.client.meetings.removeItem({ params: { co, id, item_id: item } });
    return undefined;
  });
  return (
    <div data-slot="agenda">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground">Agenda</h2>
        <span className="text-xs text-muted-foreground tabular-nums">
          {clock(groups.reduce((n, g) => n + g.spent, 0))} of{" "}
          {minutes(groups.reduce((n, g) => n + g.box, 0))}
        </span>
        {open ? (
          <span className="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={build.isPending}
              onClick={() => build.mutate(undefined)}
            >
              <WandSparklesIcon data-icon="inline-start" aria-hidden="true" />
              Build agenda
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
              <PlusIcon data-icon="inline-start" aria-hidden="true" />
              Add item
            </Button>
          </span>
        ) : null}
      </div>
      {adding ? <AddItem api={api} co={co} id={id} onClose={() => setAdding(false)} /> : null}
      {!groups.length ? (
        <EmptyState
          icon={HammerIcon}
          variant="compact"
          className="mt-6"
          title="No agenda yet"
          description={
            open
              ? "Build it from the live data: the scorecard's misses, off-track priorities, the team's issues and the to-dos due. Starting the meeting builds it too."
              : "This meeting has no agenda."
          }
          actions={
            open ? (
              <Button size="sm" onClick={() => build.mutate(undefined)}>
                <WandSparklesIcon data-icon="inline-start" aria-hidden="true" />
                Build agenda
              </Button>
            ) : undefined
          }
        />
      ) : (
        groups.map((g) => (
          <section
            key={g.section}
            className="mb-4"
            data-slot="agenda-section"
            data-section={g.section}
            aria-label={g.label}
          >
            <div className="flex h-8 items-center gap-2 border-b border-border text-sm">
              <h3 className="font-semibold text-foreground">{g.label}</h3>
              <span className="text-xs text-faint tabular-nums">{g.items.length}</span>
              <span
                className={cn("ml-auto text-xs font-medium tabular-nums", TONE_TEXT[g.tone])}
                data-slot="section-timer"
                data-tone={g.tone}
                title={`${clock(g.spent)} spent of a ${minutes(g.box)} box`}
              >
                {clock(g.spent)} / {clock(g.box)}
              </span>
            </div>
            <ul>
              {g.items.map((i, n) => (
                <AgendaRow
                  key={i.id}
                  item={i}
                  who={
                    i.seat_id && (i.source_kind === "metric" || i.source_kind === "goal")
                      ? (holders.get(i.seat_id) ?? null)
                      : null
                  }
                  meeting={m}
                  now={now}
                  editable={open}
                  selected={selected === i.id}
                  index={n}
                  last={n === g.items.length - 1}
                  onSelect={() => onSelect(i)}
                  onPatch={(body) => patch.mutate({ item: i.id, body })}
                  onRemove={() => remove.mutate(i.id)}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function AgendaRow({
  item: i,
  who,
  meeting: m,
  now,
  editable,
  selected,
  index,
  last,
  onSelect,
  onPatch,
  onRemove,
}: {
  item: AgendaItemView;
  /** The seat's holder, for a scorecard or priority line. */
  who: string | null;
  meeting: Meeting;
  now: number;
  editable: boolean;
  selected: boolean;
  /** The line's place in its section (`position` of the patch is within the section). */
  index: number;
  last: boolean;
  onSelect: () => void;
  onPatch: (body: { done?: boolean; position?: number; time_box_s?: number }) => void;
  onRemove: () => void;
}) {
  const [boxing, setBoxing] = useState(false);
  const [box, setBox] = useState("");
  const isCurrent = m.state === "live" && m.current_item_id === i.id;
  const spent = itemElapsed(i, m, now);
  const tone: TimerTone =
    spent > i.time_box_s ? (spent > i.time_box_s * 1.25 ? "late" : "over") : "ok";
  const custom = i.section === "custom" || i.section === "headlines";
  // The server's line may name the holder already (in its title or detail): not twice.
  const named = who !== null && `${i.title} · ${i.detail ?? ""}`.includes(who);
  const sub = [i.detail, named ? null : who].filter(Boolean).join(" · ");
  return (
    <li
      data-slot="agenda-item"
      data-current={isCurrent || undefined}
      data-done={i.done_at ? true : undefined}
      className={cn(
        "group/row flex min-h-11 items-center gap-2.5 border-b border-subtle pr-1 pl-2",
        selected ? "bg-primary-soft/50" : "hover:bg-sunken",
        isCurrent && "shadow-[inset_3px_0_0_var(--primary)]",
      )}
    >
      {editable ? (
        <Checkbox
          checked={!!i.done_at}
          aria-label={`${i.done_at ? "Not done" : "Done"}: ${i.title}`}
          onCheckedChange={(c) => onPatch({ done: !!c })}
        />
      ) : i.done_at ? (
        <CheckIcon aria-label="Done" className="size-4 text-success-text" />
      ) : (
        <span className="size-4" />
      )}
      <button
        type="button"
        onClick={onSelect}
        aria-current={isCurrent ? "step" : undefined}
        aria-pressed={selected}
        className="min-w-0 flex-1 py-1.5 text-left"
      >
        <span
          className={cn(
            "block truncate text-sm text-foreground",
            i.done_at && "text-muted-foreground line-through decoration-faint",
          )}
        >
          {i.title}
        </span>
        {sub ? <span className="block truncate text-xs text-muted-foreground">{sub}</span> : null}
      </button>
      {isCurrent ? <Badge variant="success">Now</Badge> : null}
      {boxing ? (
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const v = Math.round(Number(box) * 60);
            if (Number.isFinite(v) && v >= 0) onPatch({ time_box_s: v });
            setBoxing(false);
          }}
        >
          <label className="sr-only" htmlFor={`box-${i.id}`}>
            Time box of {i.title}, minutes
          </label>
          <Input
            id={`box-${i.id}`}
            type="number"
            min={0}
            autoFocus
            value={box}
            onChange={(e) => setBox(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setBoxing(false)}
            className="h-7 w-16"
          />
          <Button type="submit" size="xs" variant="secondary">
            Set
          </Button>
        </form>
      ) : (
        <span
          className={cn("w-24 shrink-0 text-right text-xs tabular-nums", TONE_TEXT[tone])}
          data-slot="item-timer"
        >
          {spent > 0 || isCurrent ? `${clock(spent)} / ` : ""}
          {minutes(i.time_box_s)}
        </span>
      )}
      {editable ? (
        <span className={HOVER_STRIP}>
          <IconAction
            label={`Move ${i.title} up`}
            tip="Move up"
            disabled={index === 0}
            onClick={() => onPatch({ position: Math.max(0, index - 1) })}
          >
            <ArrowUpIcon aria-hidden="true" />
          </IconAction>
          <IconAction
            label={`Move ${i.title} down`}
            tip="Move down"
            disabled={last}
            onClick={() => onPatch({ position: index + 1 })}
          >
            <ArrowDownIcon aria-hidden="true" />
          </IconAction>
          <IconAction
            label={`Edit the time box of ${i.title}`}
            tip="Edit time box"
            onClick={() => {
              setBox(String(Math.round(i.time_box_s / 60)));
              setBoxing(true);
            }}
          >
            <ClockIcon aria-hidden="true" />
          </IconAction>
          {custom && i.outcome_kind === "none" ? (
            <IconAction label={`Remove ${i.title}`} tip="Remove" danger onClick={onRemove}>
              <Trash2Icon aria-hidden="true" />
            </IconAction>
          ) : null}
        </span>
      ) : null}
    </li>
  );
}

function AddItem({
  api,
  co,
  id,
  onClose,
}: {
  api: Api;
  co: string;
  id: string;
  onClose: () => void;
}) {
  const fid = useId();
  const [title, setTitle] = useState("");
  const [section, setSection] = useState<"custom" | "headlines">("custom");
  const [mins, setMins] = useState("5");
  const add = useMeetingWrite(
    co,
    id,
    () =>
      api.client.meetings.addItem({
        params: { co, id },
        body: { title: title.trim(), section, time_box_s: Math.round((Number(mins) || 0) * 60) },
      }),
    onClose,
  );
  return (
    <form
      aria-label="Add agenda item"
      className="mb-4 flex flex-wrap items-end gap-2 rounded-md border border-subtle bg-card p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) add.mutate(undefined);
      }}
    >
      <Field className="min-w-48 flex-1">
        <FieldLabel htmlFor={`${fid}-t`}>Item</FieldLabel>
        <Input id={`${fid}-t`} autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field className="w-36">
        <FieldLabel htmlFor={`${fid}-s`}>Section</FieldLabel>
        <NativeSelect
          id={`${fid}-s`}
          value={section}
          onChange={(e) => setSection(e.target.value as "custom" | "headlines")}
          className="w-full"
        >
          <NativeSelectOption value="custom">Custom</NativeSelectOption>
          <NativeSelectOption value="headlines">Headlines</NativeSelectOption>
        </NativeSelect>
      </Field>
      <Field className="w-24">
        <FieldLabel htmlFor={`${fid}-m`}>Minutes</FieldLabel>
        <Input
          id={`${fid}-m`}
          type="number"
          min={0}
          value={mins}
          onChange={(e) => setMins(e.target.value)}
        />
      </Field>
      <Button type="submit" size="sm" disabled={add.isPending || !title.trim()}>
        Add
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={onClose}>
        Cancel
      </Button>
    </form>
  );
}

// Side panel ------------------------------------------------------------------------------

function SidePanel({
  api,
  company,
  detail: d,
  item,
}: {
  api: Api;
  company: CompanyAccess;
  detail: MeetingDetail;
  item: AgendaItemView | null;
}) {
  const capture = d.can_edit && d.meeting.state !== "cancelled";
  return (
    <div className="grid gap-5">
      {item ? (
        <section aria-label="Source">
          <p className="text-2xs font-semibold tracking-wide text-faint uppercase">
            {SECTION_LABELS[item.section as keyof typeof SECTION_LABELS] ?? item.section}
          </p>
          <h2 className="mt-0.5 text-base font-semibold text-foreground">{item.title}</h2>
          <div className="mt-3">
            <SourceCard company={company} source={item.source} />
          </div>
        </section>
      ) : (
        <p className="text-sm text-muted-foreground">Pick an agenda line to see it here.</p>
      )}
      {capture ? <Capture api={api} co={company.id} detail={d} item={item} /> : null}
      <Outcomes api={api} co={company.id} detail={d} />
    </div>
  );
}

const TRACK: Record<string, { label: string; badge: "success" | "warning" | "danger" }> = {
  on: { label: "On track", badge: "success" },
  at_risk: { label: "At risk", badge: "warning" },
  off: { label: "Off track", badge: "danger" },
};

export function SourceCard({
  company,
  source: s,
}: {
  company: CompanyAccess;
  source: AgendaSourceCard | null;
}) {
  if (!s)
    return (
      <p className="rounded-md border border-dashed border-border px-3 py-4 text-xs text-muted-foreground">
        A line of the team's own: no live card.
      </p>
    );
  const card = "rounded-md border border-border bg-card p-3";
  if (s.kind === "metric") {
    const pct = s.attainment === null ? null : Math.round(s.attainment * 100);
    return (
      <div className={card} data-slot="source-card" data-kind="metric">
        <p className="text-sm font-medium text-foreground">{s.name}</p>
        <p className="text-xs text-muted-foreground">
          {s.seat_title}
          {s.holder_name ? ` · ${s.holder_name}` : ""}
        </p>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-2xl font-semibold text-foreground tabular-nums">
            {formatCardMetric(s.actual, s.unit)}
          </span>
          <span className="text-sm text-muted-foreground tabular-nums">
            of {formatCardMetric(s.target, s.unit)}
          </span>
          <span className="text-xs text-muted-foreground">W{s.period_key.slice(6)}</span>
          {pct !== null ? (
            <Badge variant={pct >= 100 ? "success" : "danger"} className="ml-auto">
              {pct}%
            </Badge>
          ) : null}
        </div>
        <div className="mt-2 h-8">
          <Sparkline
            className="h-8 w-full"
            label={`${s.name}, last ${s.trend.length} weeks: ${s.trend.map((t) => formatCardMetric(t.actual, s.unit)).join(", ")}`}
            values={s.trend.map((t) => t.actual)}
            reference={s.trend.map((t) => t.target)}
          />
        </div>
        <p className="mt-1 text-2xs text-faint">{s.trend.length} weeks · dashed: target</p>
      </div>
    );
  }
  if (s.kind === "goal") {
    const track = TRACK[s.goal.track] ?? { label: s.goal.track, badge: "warning" as const };
    const done = s.goal.milestones.filter((x) => x.done_at).length;
    return (
      <div className={card} data-slot="source-card" data-kind="goal">
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 text-sm font-medium text-foreground">{s.goal.title}</p>
          <Badge variant={track.badge}>{track.label}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {s.seat_title}
          {s.holder_name ? ` · ${s.holder_name}` : ""}
        </p>
        {s.goal.milestones.length ? (
          <>
            <p className="mt-2 text-xs text-muted-foreground tabular-nums">
              Milestones {done}/{s.goal.milestones.length}
            </p>
            <ul className="mt-1 grid gap-1">
              {s.goal.milestones.map((ms) => (
                <li key={ms.id} className="flex items-center gap-1.5 text-xs">
                  {ms.done_at ? (
                    <CheckIcon aria-label="Done" className="size-3.5 text-success-text" />
                  ) : (
                    <span
                      role="img"
                      aria-label="Open"
                      className="size-3.5 rounded-full border border-border"
                    />
                  )}
                  <span className={cn(ms.done_at && "text-muted-foreground line-through")}>
                    {ms.title}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <Link
          to="/c/$slug/kpis/priorities"
          params={{ slug: company.slug }}
          search={keepFocus}
          className="mt-2 inline-block text-xs font-medium text-primary-text hover:underline"
        >
          Open Priorities
        </Link>
      </div>
    );
  }
  if (s.kind === "issue") {
    const i = s.issue;
    return (
      <div className={card} data-slot="source-card" data-kind="issue">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="accent">{i.status[0]?.toUpperCase() + i.status.slice(1)}</Badge>
          <Badge variant={i.priority === 1 ? "danger" : "neutral"}>
            {PRIORITY_LABELS[i.priority]}
          </Badge>
          {i.escalated_to === "company" ? <Badge variant="warning">Escalated</Badge> : null}
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {ageLabel(i.age_days)} old
          </span>
        </div>
        <p className="mt-1.5 text-sm font-medium text-foreground">{i.title}</p>
        <p className="text-xs text-muted-foreground">
          {i.owner_title
            ? `${i.owner_title}${i.owner_name ? ` · ${i.owner_name}` : ""}`
            : "No owner"}
        </p>
        {i.source_label ? <p className="mt-1 text-xs text-faint">{i.source_label}</p> : null}
      </div>
    );
  }
  const t = s.task;
  return (
    <div className={card} data-slot="source-card" data-kind="task">
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground tabular-nums">#{t.number}</span>
        <Badge variant={t.done ? "success" : "accent"}>{t.status.replaceAll("_", " ")}</Badge>
        {t.due_at ? (
          <span
            className={cn(
              "ml-auto text-xs tabular-nums",
              !t.done && Date.parse(t.due_at) < Date.now()
                ? "text-danger-text"
                : "text-muted-foreground",
            )}
          >
            Due {new Date(t.due_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        ) : null}
      </div>
      <p className="mt-1.5 text-sm font-medium text-foreground">{t.title}</p>
    </div>
  );
}

type CaptureKind = "task" | "decision" | "solve" | "issue";

function Capture({
  api,
  co,
  detail: d,
  item,
}: {
  api: Api;
  co: string;
  detail: MeetingDetail;
  item: AgendaItemView | null;
}) {
  const seats = useSeatOptions(api, co);
  const [kind, setKind] = useState<CaptureKind | null>(null);
  const issue = item?.source?.kind === "issue" ? item.source.issue : null;
  const solvable = issue && ["identify", "discuss", "solve"].includes(issue.status);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new line closes the open form
  useEffect(() => setKind(null), [item?.id]);
  const button = (k: CaptureKind, label: string) => (
    <Button
      type="button"
      size="xs"
      variant={kind === k ? "default" : "secondary"}
      aria-pressed={kind === k}
      onClick={() => setKind(kind === k ? null : k)}
    >
      {label}
    </Button>
  );
  return (
    <section aria-label="Capture an outcome" data-slot="capture">
      <p className="text-2xs font-semibold tracking-wide text-faint uppercase">Capture</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {button("task", "+ To-do")}
        {button("decision", "+ Decision")}
        {solvable ? button("solve", "Solve issue") : null}
        {button("issue", "+ Issue")}
      </div>
      {kind ? (
        <CaptureForm
          key={`${kind}-${item?.id ?? ""}`}
          api={api}
          co={co}
          id={d.meeting.id}
          kind={kind}
          item={item}
          issueId={issue?.id ?? null}
          seats={seats}
          onDone={() => setKind(null)}
        />
      ) : null}
    </section>
  );
}

function CaptureForm({
  api,
  co,
  id,
  kind,
  item,
  issueId,
  seats,
  onDone,
}: {
  api: Api;
  co: string;
  id: string;
  kind: CaptureKind;
  item: AgendaItemView | null;
  issueId: string | null;
  seats: readonly SeatOption[];
  onDone: () => void;
}) {
  const fid = useId();
  const [text, setText] = useState("");
  const [why, setWhy] = useState("");
  const [expected, setExpected] = useState("");
  const [checkDays, setCheckDays] = useState("14");
  const [seat, setSeat] = useState(item?.seat_id ?? "");
  const [due, setDue] = useState(defaultDue());
  const record = useMeetingWrite(
    co,
    id,
    () => {
      const item_id = item?.id ?? null;
      const body =
        kind === "task"
          ? {
              kind: "task" as const,
              item_id,
              title: text.trim(),
              assignee_seat_id: seat || null,
              due_at: dueInstant(due),
            }
          : kind === "decision"
            ? {
                kind: "decision" as const,
                item_id,
                decision: text.trim(),
                why: why.trim() || null,
                expected_outcome: expected.trim() || null,
                ...(checkDaysOf(checkDays) ? { check_in_days: checkDaysOf(checkDays) } : {}),
              }
            : kind === "solve"
              ? {
                  kind: "issue_resolved" as const,
                  item_id,
                  issue_id: issueId ?? "",
                  note: text.trim() || null,
                }
              : {
                  kind: "issue_raised" as const,
                  item_id,
                  title: text.trim(),
                  owner_seat_id: seat || null,
                };
      return api.client.meetings.recordOutcome({ params: { co, id }, body });
    },
    onDone,
  );
  const needsText = kind !== "solve";
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!needsText || text.trim()) record.mutate(undefined);
  };
  const label =
    kind === "task"
      ? "To-do"
      : kind === "decision"
        ? "Decision"
        : kind === "solve"
          ? "Note (optional)"
          : "Issue";
  return (
    <form
      aria-label={
        kind === "task"
          ? "New to-do"
          : kind === "decision"
            ? "New decision"
            : kind === "solve"
              ? "Solve issue"
              : "New issue"
      }
      className="mt-2 grid gap-2 rounded-md border border-border bg-card p-3"
      onSubmit={submit}
    >
      <Field>
        <FieldLabel htmlFor={`${fid}-t`}>{label}</FieldLabel>
        {kind === "decision" ? (
          <Textarea
            id={`${fid}-t`}
            rows={2}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        ) : (
          <Input id={`${fid}-t`} autoFocus value={text} onChange={(e) => setText(e.target.value)} />
        )}
      </Field>
      {kind === "decision" ? (
        <>
          <Field>
            <FieldLabel htmlFor={`${fid}-w`}>Why (optional)</FieldLabel>
            <Input id={`${fid}-w`} value={why} onChange={(e) => setWhy(e.target.value)} />
          </Field>
          <ExpectedFields
            id={fid}
            expected={expected}
            onExpected={setExpected}
            days={checkDays}
            onDays={setCheckDays}
          />
        </>
      ) : null}
      {kind === "task" || kind === "issue" ? (
        <Field>
          <FieldLabel htmlFor={`${fid}-s`}>
            {kind === "task" ? "Assignee" : "Owner seat"}
          </FieldLabel>
          <NativeSelect
            id={`${fid}-s`}
            size="sm"
            value={seat}
            onChange={(e) => setSeat(e.target.value)}
            className="w-full"
          >
            <NativeSelectOption value="">
              {kind === "task" ? "Unassigned" : "No owner"}
            </NativeSelectOption>
            {seats.map((s) => (
              <NativeSelectOption key={s.id} value={s.id}>
                {s.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      ) : null}
      {kind === "task" ? (
        <Field>
          <FieldLabel htmlFor={`${fid}-d`}>Due</FieldLabel>
          <Input id={`${fid}-d`} type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </Field>
      ) : null}
      {record.isError ? (
        <p role="alert" className="text-xs text-danger-text">
          {problemText(record.error, "The outcome could not be saved.")}
        </p>
      ) : null}
      <div className="flex justify-end gap-1.5">
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={record.isPending || (needsText && !text.trim())}>
          {kind === "task"
            ? "Add to-do"
            : kind === "decision"
              ? "Log decision"
              : kind === "solve"
                ? "Solve"
                : "Raise issue"}
        </Button>
      </div>
    </form>
  );
}

function Outcomes({ api, co, detail: d }: { api: Api; co: string; detail: MeetingDetail }) {
  const todos = d.outcomes.filter((o) => o.kind === "task");
  const issues = d.outcomes.filter((o) => o.kind === "issue_resolved" || o.kind === "issue_raised");
  const decisions = d.decisions;
  const total = todos.length + issues.length + decisions.length;
  return (
    <section aria-label="Outcomes" data-slot="outcomes">
      <p className="text-2xs font-semibold tracking-wide text-faint uppercase">
        Outcomes <span className="tabular-nums">{total}</span>
      </p>
      {!total ? (
        <p className="mt-1 text-xs text-muted-foreground">Nothing captured yet.</p>
      ) : (
        <div className="mt-1.5 grid gap-3 text-sm">
          {todos.length ? (
            <OutcomeGroup label="To-dos" items={todos.map((o) => ({ id: o.id, text: o.title }))} />
          ) : null}
          {decisions.length ? (
            <DecisionGroup
              api={api}
              co={co}
              items={decisions.map((x) => ({ id: x.id, text: x.decision, sub: x.why }))}
            />
          ) : null}
          {issues.length ? (
            <OutcomeGroup
              label="Issues"
              items={issues.map((o) => ({
                id: o.id,
                text: o.title,
                sub: o.kind === "issue_resolved" ? "Solved" : "Raised",
              }))}
            />
          ) : null}
        </div>
      )}
    </section>
  );
}

function OutcomeGroup({
  label,
  items,
}: {
  label: string;
  items: { id: string; text: string; sub?: string | null }[];
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <ul className="mt-0.5 grid gap-1">
        {items.map((i) => (
          <li key={i.id} className="flex items-start gap-1.5">
            <CircleDotIcon aria-hidden="true" className="mt-1 size-3 shrink-0 text-faint" />
            <span className="min-w-0">
              <span className="text-foreground">{i.text}</span>
              {i.sub ? <span className="block text-xs text-muted-foreground">{i.sub}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** RECALL-KPIS-MIRROR: what memory did with a stance, in words. */
const STANCE_SENT: Record<string, string> = {
  sent: "Saved, and memory has it.",
  waiting: "Saved; memory takes it later.",
  refused: "Saved; memory refused it.",
  local: "Saved; this decision is not in memory.",
};

/** RECALL-KPIS-MIRROR: logged decisions, each with Agree / Object as hover icons. */
function DecisionGroup({
  api,
  co,
  items,
}: {
  api: Api;
  co: string;
  items: { id: string; text: string; sub?: string | null }[];
}) {
  const [objecting, setObjecting] = useState<string | null>(null);
  const [why, setWhy] = useState("");
  const fid = useId();
  const stance = useMutation({
    mutationFn: (v: { id: string; stance: "ratify" | "dissent"; note?: string }) =>
      api.client.decisionStances.set({
        params: { co, id: v.id },
        body:
          v.stance === "dissent" ? { stance: "dissent", note: v.note ?? "" } : { stance: "ratify" },
      }),
    onSuccess: (r) => {
      toast.add({
        title: r.mine?.stance === "dissent" ? "Objection saved" : "Agreement saved",
        description: STANCE_SENT[r.mine?.recall_state ?? "sent"] ?? "Saved.",
        type: "success",
      });
      setObjecting(null);
      setWhy("");
    },
    onError: (e) =>
      toast.add({ title: "Not saved", description: problemText(e, "Try again."), type: "error" }),
  });
  return (
    <div data-slot="decision-group">
      <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        Decisions
        <InfoTip label="About agree and object">
          Agree or object to a decision. It stands either way; your objection and why are kept with
          it, and in memory.
        </InfoTip>
      </p>
      <ul className="mt-0.5 grid gap-1">
        {items.map((i) => (
          <li key={i.id} className="group/row flex items-start gap-1.5" data-decision={i.id}>
            <CircleDotIcon aria-hidden="true" className="mt-1 size-3 shrink-0 text-faint" />
            <span className="min-w-0 flex-1">
              <span className="text-foreground">{i.text}</span>
              {i.sub ? <span className="block text-xs text-muted-foreground">{i.sub}</span> : null}
            </span>
            <span className={HOVER_STRIP}>
              <IconAction
                label="Agree"
                tip="Agree with this decision"
                disabled={stance.isPending}
                onClick={() => stance.mutate({ id: i.id, stance: "ratify" })}
              >
                <ThumbsUpIcon aria-hidden="true" />
              </IconAction>
              <IconAction
                label="Object"
                tip="Object, and say why"
                onClick={() => setObjecting(i.id)}
              >
                <FlagIcon aria-hidden="true" />
              </IconAction>
            </span>
          </li>
        ))}
      </ul>
      <Dialog open={objecting !== null} onOpenChange={(o) => (o ? null : setObjecting(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Object to this decision</DialogTitle>
            <DialogDescription>
              The decision stands; your objection is kept with it.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={fid}>Why</FieldLabel>
            <Textarea
              id={fid}
              rows={3}
              maxLength={300}
              value={why}
              onChange={(e) => setWhy(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary">Cancel</Button>} />
            <Button
              disabled={!why.trim() || stance.isPending || objecting === null}
              onClick={() =>
                objecting && stance.mutate({ id: objecting, stance: "dissent", note: why.trim() })
              }
            >
              Object
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** RECALL-KPIS-MIRROR: memory's team digest and agenda, read before the meeting (input only). */
function MemoryInput({ memory }: { memory: NonNullable<MeetingDetail["preread"]["memory"]> }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-slot="preread-memory">
      <div className="flex items-center gap-1">
        <h2 className="text-sm font-semibold text-foreground">From memory</h2>
        <InfoTip label="About memory input">
          What memory knew about the team before the meeting: its weekly digest and agenda. Input
          for the pre-read only; the agenda here is the meeting's own.
        </InfoTip>
        <span className="ml-auto text-xs text-muted-foreground">
          {new Date(memory.read_at).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
      {memory.state === "unavailable" ? (
        <p className="mt-2 text-sm text-muted-foreground">{memory.detail}</p>
      ) : (
        <div className="mt-2 grid gap-3">
          {[memory.digest, memory.agenda].map((t, n) =>
            t ? (
              <pre
                key={n === 0 ? "digest" : "agenda"}
                className="whitespace-pre-wrap break-words font-sans text-sm text-foreground"
              >
                {t}
              </pre>
            ) : null,
          )}
        </div>
      )}
    </div>
  );
}

// Pre-read, notes, end ---------------------------------------------------------------------

function PreRead({ api, co, detail: d }: { api: Api; co: string; detail: MeetingDetail }) {
  const p = d.preread;
  const id = d.meeting.id;
  const fid = useId();
  const [note, setNote] = useState(p.note ?? "");
  useEffect(() => setNote(p.note ?? ""), [p.note]);
  const ask = useMeetingWrite(co, id, () =>
    api.client.meetings.requestPreread({ params: { co, id } }),
  );
  const save = useMeetingWrite(co, id, () =>
    api.client.meetings.update({ params: { co, id }, body: { preread_note: note.trim() || null } }),
  );
  const shown = !p.agent?.live && p.state !== "ready" && p.state !== "none" ? "pending" : p.state;
  const st = PREREAD_STATE[shown];
  const open = d.meeting.state !== "ended" && d.meeting.state !== "cancelled";
  const copy =
    !p.agent?.live && shown !== "ready"
      ? "No digital employee of the team is live; the pre-read will be prepared when one is."
      : shown === "requested"
        ? `${p.agent?.name ?? "The team's digital employee"} was asked${p.requested_at ? ` ${new Date(p.requested_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""} and is preparing it.`
        : shown === "ready"
          ? "The pre-read is ready."
          : `${p.agent?.name ?? "The team's digital employee"} prepares the pre-read the day before the meeting.`;
  return (
    <div className="mx-auto grid max-w-2xl gap-4" data-slot="preread">
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">Pre-read</h2>
          <Badge variant={st.badge} data-slot="preread-state">
            {st.label}
          </Badge>
          {p.agent ? (
            <span className="text-xs text-muted-foreground">
              {p.agent.name} · {p.agent.live ? "live" : "not live"}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">No digital employee on this team</span>
          )}
          {d.can_edit && open ? (
            <Button
              size="sm"
              variant="secondary"
              className="ml-auto"
              disabled={ask.isPending}
              onClick={() => ask.mutate(undefined)}
            >
              Ask for the pre-read now
            </Button>
          ) : null}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{copy}</p>
      </div>
      {p.memory ? <MemoryInput memory={p.memory} /> : null}
      <Field>
        <FieldLabel htmlFor={fid}>Pre-read note</FieldLabel>
        <Textarea
          id={fid}
          rows={8}
          value={note}
          readOnly={!d.can_edit}
          placeholder={
            d.can_edit ? "What everyone should read before the meeting." : "No pre-read yet."
          }
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      {d.can_edit ? (
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={save.isPending || note === (p.note ?? "")}
            onClick={() => save.mutate(undefined)}
          >
            Save the note
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Notes({ api, co, detail: d }: { api: Api; co: string; detail: MeetingDetail }) {
  const qc = useQueryClient();
  const id = d.meeting.id;
  const fid = useId();
  const [text, setText] = useState(d.meeting.notes);
  const [saved, setSaved] = useState<"idle" | "saving" | "saved">("idle");
  const timer = useRef<number | null>(null);
  const save = useMutation({
    mutationFn: (notes: string) =>
      api.client.meetings.update({ params: { co, id }, body: { notes } }),
    onMutate: () => setSaved("saving"),
    onSuccess: (detail) => {
      qc.setQueryData(cadenceKeys.meeting(co, id), detail);
      setSaved("saved");
    },
    onError: () => setSaved("idle"),
  });
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );
  const change = (v: string) => {
    setText(v);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => save.mutate(v), 800);
  };
  return (
    <div className="mx-auto grid max-w-2xl gap-2" data-slot="notes">
      <div className="flex items-center gap-2">
        <label htmlFor={fid} className="text-sm font-semibold text-foreground">
          Notes
        </label>
        {d.meeting.rating ? <Badge variant="accent">Rated {d.meeting.rating}/10</Badge> : null}
        <span className="ml-auto text-xs text-muted-foreground" aria-live="polite">
          {saved === "saving" ? "Saving…" : saved === "saved" ? "Saved" : ""}
        </span>
      </div>
      <Textarea
        id={fid}
        rows={16}
        value={text}
        readOnly={!d.can_edit}
        placeholder={d.can_edit ? "Notes are saved as you type." : "No notes."}
        onChange={(e) => change(e.target.value)}
      />
    </div>
  );
}

function EndDialog({
  api,
  co,
  meeting: m,
  onClose,
}: {
  api: Api;
  co: string;
  meeting: Meeting;
  onClose: () => void;
}) {
  const fid = useId();
  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState(m.notes);
  const end = useMeetingWrite(
    co,
    m.id,
    () =>
      api.client.meetings.end({
        params: { co, id: m.id },
        body: { notes, ...(rating ? { rating } : {}) },
      }),
    onClose,
  );
  return (
    <Dialog open onOpenChange={(o) => !o && !end.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>End the meeting</DialogTitle>
          <DialogDescription>
            The summary (to-dos, decisions, issues, notes) posts to the team's channel, and the next
            meeting is scheduled.
          </DialogDescription>
        </DialogHeader>
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-foreground">
            How was the meeting? (1–10)
          </legend>
          <div className="flex flex-wrap gap-1" data-slot="rating">
            {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => (
              <Button
                key={n}
                type="button"
                size="icon-sm"
                variant={rating === n ? "default" : "outline"}
                aria-pressed={rating === n}
                aria-label={`Rate ${n} out of 10`}
                onClick={() => setRating(n)}
              >
                {n}
              </Button>
            ))}
          </div>
        </fieldset>
        <Field>
          <FieldLabel htmlFor={fid}>Notes</FieldLabel>
          <Textarea id={fid} rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {end.isError ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(end.error, "The meeting could not be ended.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Keep going</DialogClose>
          <Button
            variant="destructive"
            disabled={end.isPending}
            onClick={() => end.mutate(undefined)}
          >
            End meeting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
